/* WARNING: Keep this file free of imports and free of side effects at
 * module load. The server and the client both import it, and the build also
 * compiles it on its own into `migrate/.generated/`, so that the backfill
 * and reconcile scripts share exactly one copy of the role vocabulary.
 * `entities.ts` re-exports everything here, so every
 * `import { ROLE } from "./entities"` site still works unchanged. */

export const PUBLIC_ID = "public";
export const SHARE_KEY_PREFIX = "share_";
// eslint-disable-next-line no-useless-escape
export const SHARE_KEY_REGEX = /^share[\|\_]/;

/**
 * The prefix on the identity key of an email invite that the invited user
 * did not accept yet: `invite:<email>`. It is a named constant so that
 * `isInviteId`, `getInviteEmail`, `UserAutocomplete.tsx`,
 * `InviteByEmail.tsx` and `getUserPublic.ts` do not each hard-code the
 * literal.
 */
export const INVITE_KEY_PREFIX = "invite:";

export type Id = string | "public";

export enum ROLE {
  VIEW = "role_VIEW",
  COMMENT = "role_COMMENT",
  REPLY = "role_REPLY",
  REACT = "role_REACT",
  DOWNLOAD = "role_DOWNLOAD",
  UPLOAD = "role_UPLOAD",
  MANAGE = "role_MANAGE",
  ADMIN = "role_ADMIN",

  /**
   * This role does not map to any action that a user can take. The
   * share-link checks use it to record whether anonymous users can open a
   * share link.
   */
  GUESTS_ALLOWED = "role_GUESTS_ALLOWED",
}

export const COMPATIBLE_ROLES: Record<ROLE, ROLE[]> = Object.freeze({
  [ROLE.VIEW]: [
    ROLE.VIEW,
    ROLE.COMMENT,
    ROLE.REPLY,
    ROLE.REACT,
    ROLE.DOWNLOAD,
    ROLE.UPLOAD,
    ROLE.MANAGE,
    ROLE.ADMIN,
  ],
  [ROLE.COMMENT]: [ROLE.COMMENT, ROLE.MANAGE, ROLE.ADMIN],
  [ROLE.REPLY]: [ROLE.REPLY, ROLE.MANAGE, ROLE.ADMIN],
  [ROLE.REACT]: [ROLE.REACT, ROLE.MANAGE, ROLE.ADMIN],
  [ROLE.DOWNLOAD]: [ROLE.DOWNLOAD, ROLE.MANAGE, ROLE.ADMIN],
  [ROLE.UPLOAD]: [ROLE.UPLOAD, ROLE.MANAGE, ROLE.ADMIN],
  [ROLE.GUESTS_ALLOWED]: [ROLE.GUESTS_ALLOWED, ROLE.MANAGE, ROLE.ADMIN],
  [ROLE.MANAGE]: [ROLE.MANAGE, ROLE.ADMIN],
  /*
   * Each entity has exactly one admin: the user in its `createdBy` field.
   */
  [ROLE.ADMIN]: [ROLE.ADMIN],
});

export type Roles = Record<Id, ROLE[]>;

/**
 * The literal array that `getFullRoles` (`shared-util.ts`) and
 * `computeEffectiveRoles` (`membership.ts`) put in place of an inherited
 * `role_ADMIN` grant. This is the "anyone who owns the parent collection is
 * downgraded to Manage" rule, which was an anonymous inline array inside
 * `getFullRoles` before.
 *
 * The order and the membership of this array are a contract that other
 * code checks: the reconcile script
 * (`migrate/spaceMembership/3-reconcile.js`) compares `effectiveRoles`
 * element by element, and mobile's `MemberRolePreset.manager` matches it as
 * an exact set.
 *
 * The array happens to equal `COMPATIBLE_ROLES[ROLE.VIEW]` minus `ADMIN`.
 * Do not define it that way: a definition that reads `COMPATIBLE_ROLES`
 * lets a future edit of that table silently change what every inherited
 * admin can do.
 */
export const INHERITED_ADMIN_ROLES: readonly ROLE[] = Object.freeze([
  ROLE.MANAGE,
  ROLE.COMMENT,
  ROLE.REPLY,
  ROLE.REACT,
  ROLE.DOWNLOAD,
  ROLE.UPLOAD,
  ROLE.VIEW,
]);

/**
 * The kind of identity a membership document belongs to. The writer derives
 * it from the document id with `classifyIdentityKey` (`membership.ts`); no
 * other field stores it.
 *
 * An anonymous Firebase uid is an opaque string with no `|`, so
 * `classifyIdentityKey` returns `USER` for it. Every legacy field treats
 * such uids the same way.
 */
export enum SPACE_MEMBER_TYPE {
  USER = "user",
  SHARE = "share",
  INVITE = "invite",
  PUBLIC = "public",
}

/**
 * The membership subcollection under a space:
 * `spaces-v1/{spaceId}/members-v1/{memberId}`.
 *
 * This id is deliberately not a member of `COLLECTION_ID`. Every value in
 * that enum is a top-level collection id. Three generic triggers listen on
 * the two-segment wildcard `/{collectionId}/{entityId}`
 * (`onWriteHooks/onWrite.ts`, `onCreate.ts`, `onDelete.ts`). A member
 * document lives at a four-segment path, so those triggers never see it.
 * That is the behaviour we want: no S3 mirror of share keys or invite
 * emails, and no `writeToAWS` PUT for each membership write. If somebody
 * adds `members-v1` to `COLLECTION_ID`, a call like
 * `getEntity({ collectionId, entityId })` can then create a real top-level
 * `members-v1` collection, and that collection does match those wildcards.
 */
export const MEMBERS_COLLECTION_ID = "members-v1";
